import * as Yup from 'yup';

export const QUOTE_NO_REGEX = /^\d{6}$/;
export const QUOTE_YEAR_REGEX = /^(20\d{2})$/;
export const QUOTATION_CODE_REGEX = /^\d{6}-\d{4}$/;
export const PROPOSAL_CODE_REGEX = /^\d{9}$/;
export const POLICY_NUMBER_REGEX = /^\d{15}$/;

export interface ParsedQuotationCode {
  quoteNo: string;
  quoteYear: string;
}

export interface BatchCodesFormValues {
  quoteNo: string;
  quoteYear: string;
  proposalCode: string;
  policyNumber: string;
  status: string;
}

export interface BatchCodesSource {
  quotationCode: string;
  proposalCode: string | null;
  policyNumber: string | null;
  status: string;
}

export function parseQuotationCode(code: string): ParsedQuotationCode | null {
  if (!code?.trim() || !QUOTATION_CODE_REGEX.test(code.trim())) {
    return null;
  }

  const [quoteNo, quoteYear] = code.trim().split('-');
  return { quoteNo, quoteYear };
}

export function buildQuotationCode(quoteNo: string, quoteYear: string): string {
  return `${quoteNo}-${quoteYear}`;
}

export function normalizeBatchStatus(status: string): string {
  return status === 'CANCELED' ? 'CANCELLED' : status;
}

export function buildFormValuesFromBatch(batch: BatchCodesSource): BatchCodesFormValues {
  const parsed = parseQuotationCode(batch.quotationCode || '');

  return {
    quoteNo: parsed?.quoteNo || '',
    quoteYear: parsed?.quoteYear || '',
    proposalCode: batch.proposalCode || '',
    policyNumber: batch.policyNumber || '',
    status: normalizeBatchStatus(batch.status || ''),
  };
}

export function hasBatchCodesChanged(
  original: BatchCodesFormValues,
  current: BatchCodesFormValues,
): boolean {
  return (
    original.quoteNo !== current.quoteNo ||
    original.quoteYear !== current.quoteYear ||
    original.proposalCode !== current.proposalCode ||
    original.policyNumber !== current.policyNumber ||
    original.status !== current.status
  );
}

export function stripDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

export const BATCH_STATUS_VALUES = [
  'OPENED',
  'PROCESSING',
  'AWAITING_POLICY_EMISSION',
  'ERROR',
  'PENDING',
  'CLOSED',
  'CANCELLED',
] as const;

export const batchCodesSchema = Yup.object({
  quoteNo: Yup.string()
    .required('Informe o número da cotação')
    .matches(QUOTE_NO_REGEX, 'Deve conter exatamente 6 dígitos numéricos'),
  quoteYear: Yup.string()
    .required('Informe o ano da cotação')
    .matches(QUOTE_YEAR_REGEX, 'Ano inválido (use 4 dígitos, ex.: 2025)'),
  proposalCode: Yup.string().test(
    'proposal-format',
    'Deve conter exatamente 9 dígitos numéricos',
    (value) => !value || PROPOSAL_CODE_REGEX.test(value),
  ),
  policyNumber: Yup.string().test(
    'policy-format',
    'Deve conter exatamente 15 dígitos numéricos',
    (value) => !value || POLICY_NUMBER_REGEX.test(value),
  ),
  status: Yup.string()
    .required('Selecione o status do lote')
    .oneOf([...BATCH_STATUS_VALUES], 'Status inválido'),
});

export type BatchCodesFormErrors = Partial<Record<keyof BatchCodesFormValues, string>>;

export async function validateBatchCodesForm(
  values: BatchCodesFormValues,
): Promise<BatchCodesFormErrors> {
  try {
    await batchCodesSchema.validate(values, { abortEarly: false });
    return {};
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) {
      throw error;
    }

    const errors: BatchCodesFormErrors = {};
    error.inner.forEach((item) => {
      if (item.path) {
        errors[item.path as keyof BatchCodesFormValues] = item.message;
      }
    });
    return errors;
  }
}
